import type Phaser from 'phaser';
import { DENOMINATIONS_CENTS } from '../config.js';

export interface ChipBreakdownEntry {
  denom: number;
  count: number;
}

/**
 * Greedy denomination breakdown: largest denomination first. Returns only
 * entries with count > 0. Returns [] for amounts <= 0, or for amounts smaller
 * than the smallest denomination (the leftover sub-chip remainder is dropped).
 *
 * @param cents - Must be a non-negative integer (whole cents). Non-integer
 *   inputs produce undefined results.
 */
export function decomposeBet(cents: number): ChipBreakdownEntry[] {
  if (cents <= 0) return [];
  const denoms = [...DENOMINATIONS_CENTS].sort((a, b) => b - a);
  const out: ChipBreakdownEntry[] = [];
  let remaining = cents;
  for (const d of denoms) {
    const count = Math.floor(remaining / d);
    if (count > 0) {
      out.push({ denom: d, count });
      remaining -= count * d;
    }
  }
  return out;
}

export interface ChipPalette {
  body: number;
  spot: number;
  inlayBorder: number;
  textColor: string; // CSS color for the denomination number
}

// Inlay is always white, so textColor must contrast with white. We pick a dark
// color that ties back to the chip's body color (e.g. red text on the red $5).
const PALETTES: Record<number, ChipPalette> = {
  100: {
    body: 0xf5f5f5,
    spot: 0xc0392b,
    inlayBorder: 0xc0392b,
    textColor: '#222',
  },
  500: {
    body: 0xc0392b,
    spot: 0xffffff,
    inlayBorder: 0xc0392b,
    textColor: '#9a1f15',
  },
  2500: {
    body: 0x1f8b4c,
    spot: 0xffb6c1,
    inlayBorder: 0x1f8b4c,
    textColor: '#125a30',
  },
  10000: {
    body: 0x111111,
    spot: 0xffffff,
    inlayBorder: 0xd4af37,
    textColor: '#111',
  },
  50000: {
    body: 0x5b2d83,        // purple
    spot: 0xd4af37,        // gold spots
    inlayBorder: 0xd4af37, // gold inlay rim
    textColor: '#3d1858',
  },
  100000: {
    body: 0xd4a017,        // goldenrod
    spot: 0xffffff,
    inlayBorder: 0x7c1f1d, // deep red inlay rim
    textColor: '#7c1f1d',
  },
  500000: {
    body: 0x3d4759,        // slate-gray
    spot: 0x7c1f1d,        // burgundy spots
    inlayBorder: 0xd4af37, // gold rim
    textColor: '#1a2230',
  },
};

export function paletteForCents(cents: number): ChipPalette {
  const palette = PALETTES[cents];
  if (!palette) throw new Error(`Chip: no palette for denomination ${cents} cents`);
  return palette;
}

function labelFor(cents: number): string {
  const dollars = Math.round(cents / 100);
  if (dollars >= 1000) return `${dollars / 1000}K`;
  return String(dollars);
}

const CHIP_TEXTURE_RADIUS = 64; // px in canvas; sprites scale down for display
const SHADOW_OFFSET_Y = 4;
const SHADOW_PADDING = 8; // padding on every side so the shadow isn't clipped
const TEXTURE_SIZE = (CHIP_TEXTURE_RADIUS + SHADOW_PADDING) * 2;

const NUM_SPOTS = 8;
const SPOT_HALF_ARC_RAD = (14 * Math.PI) / 360; // ~14° wedge per spot

function chipTextureKey(cents: number): string {
  return `chip-${cents}`;
}

function hexToCss(n: number): string {
  return '#' + n.toString(16).padStart(6, '0');
}

function drawChip(
  ctx: CanvasRenderingContext2D,
  palette: ChipPalette,
  label: string,
) {
  const cx = TEXTURE_SIZE / 2;
  const cy = TEXTURE_SIZE / 2;
  const r = CHIP_TEXTURE_RADIUS;

  // Drop shadow
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.arc(cx, cy + SHADOW_OFFSET_Y, r, 0, Math.PI * 2);
  ctx.fill();

  // Body
  ctx.fillStyle = hexToCss(palette.body);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  // Edge spots — 8 wedges every 45°, between outer rim and 0.78 r
  ctx.fillStyle = hexToCss(palette.spot);
  for (let i = 0; i < NUM_SPOTS; i++) {
    const a = (i * Math.PI * 2) / NUM_SPOTS;
    ctx.beginPath();
    ctx.arc(cx, cy, r, a - SPOT_HALF_ARC_RAD, a + SPOT_HALF_ARC_RAD, false);
    ctx.arc(cx, cy, r * 0.78, a + SPOT_HALF_ARC_RAD, a - SPOT_HALF_ARC_RAD, true);
    ctx.closePath();
    ctx.fill();
  }

  // Outer rim line
  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();

  // Inner highlight ring (bevel cue)
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.76, 0, Math.PI * 2);
  ctx.stroke();

  // Inlay disc
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.62, 0, Math.PI * 2);
  ctx.fill();

  // Inlay border
  ctx.strokeStyle = hexToCss(palette.inlayBorder);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.62, 0, Math.PI * 2);
  ctx.stroke();

  // Denomination number — start at the height-based size, then shrink to fit
  // width if needed (so "100" doesn't spill onto the chip body), and center on
  // the visual glyph bounds (Arial Black digits drift low under 'middle').
  const inlayRadius = r * 0.62;
  const maxTextWidth = inlayRadius * 1.7; // 85% of the inlay diameter
  let fontPx = Math.round(inlayRadius * 1.0);
  ctx.fillStyle = palette.textColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `bold ${fontPx}px "Arial Black", Helvetica, sans-serif`;
  let m = ctx.measureText(label);
  while (m.width > maxTextWidth && fontPx > 8) {
    fontPx -= 1;
    ctx.font = `bold ${fontPx}px "Arial Black", Helvetica, sans-serif`;
    m = ctx.measureText(label);
  }
  const visualOffset = (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
  ctx.fillText(label, cx, cy + visualOffset);
}

/**
 * Returns a texture key for the given denomination. On first call per
 * denomination, draws the chip into an offscreen canvas and registers it with
 * Phaser's TextureManager. Subsequent calls return the cached key.
 */
export function getChipTexture(scene: Phaser.Scene, cents: number): string {
  const key = chipTextureKey(cents);
  if (scene.textures.exists(key)) return key;

  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Chip canvas 2D context unavailable');

  drawChip(ctx, paletteForCents(cents), labelFor(cents));
  scene.textures.addCanvas(key, canvas);
  return key;
}

/**
 * Returns a sprite at the given display radius (visible radius in CSS pixels),
 * referencing the cached chip texture for the denomination.
 */
export function createChipSprite(
  scene: Phaser.Scene,
  cents: number,
  displayRadius: number,
): Phaser.GameObjects.Sprite {
  const key = getChipTexture(scene, cents);
  const sprite = scene.add.sprite(0, 0, key);
  sprite.setScale(displayRadius / CHIP_TEXTURE_RADIUS);
  return sprite;
}
